import React from 'react'
import styled from '@emotion/styled'
import { getRaceDetail } from '../../integration'
import { SelectAcceptor } from '../../states/signals'
import type { IFixedOdds, IAcceptorBase, IRaceDetail } from '../../types'

export type OddsType = 'fixed' | 'tote'

export const OddsContext = React.createContext<{ currentOdds: OddsType; toggleOdds(): void }>({
  currentOdds: 'fixed',
  toggleOdds: () => null,
})

export const guOddsAsString = (odds?: number) => (odds ? `$${odds.toFixed(2)}` : '-')

export const OddsPrice: React.FC<{
  acceptor: IAcceptorBase
  hidePlacePrice?: boolean
}> = ({ acceptor, hidePlacePrice }) => {
  const oddsContext = React.useContext(OddsContext)

  const transform = `translateX(-${(oddsContext.currentOdds === 'tote' ? 0 : 1) * 50}%)`
  return (
    <OddsContainerStyled style={{ overflow: 'hidden' }}>
      <div data-tid-odds className='odds' style={{ transform, WebkitTransform: transform }}>
        <ToteOdds acceptor={acceptor} hidePlacePrice={hidePlacePrice} />
        <FixedOdds acceptor={acceptor} hidePlacePrice={hidePlacePrice} />
      </div>
    </OddsContainerStyled>
  )
}

export const OddsToggle = () => {
  const id = React.useId()
  const oddsContext = React.useContext(OddsContext)

  return (
    <div className='alignmid' data-tid-odds-toggle onClick={oddsContext.toggleOdds}>
      <FobToteSwitchStyled viewBox='0 0 50 30'>
        <rect
          x='0'
          y='0'
          width='50'
          height='30'
          fill='#FFF'
          rx='15'
          ry='15'
          stroke='#CCC'
          strokeWidth='2'
          strokeLinecap='butt'
        ></rect>
        <text x='15' y='20' textAnchor='middle' color='#AAA' fill='#AAA'>
          {' '}
          F
        </text>
        <text x='39' y='20' textAnchor='end' color='#AAA' fill='#AAA'>
          {' '}
          T
        </text>
        <defs>
          <clipPath id={id}>
            <circle
              className='oddsToggle'
              cx={oddsContext.currentOdds === 'tote' ? 35 : 15}
              cy='15'
              r='12'
            />
          </clipPath>
        </defs>
        <g clipPath={`url(#${id})`}>
          <rect
            x='0'
            y='0'
            width='100'
            height='30'
            fill={oddsContext.currentOdds === 'tote' ? '#542d6b' : '#30878E'}
          ></rect>
          <text x='15' y='20' textAnchor='middle' color='#FFFFFF' fill='#FFFFFF'>
            {' '}
            F
          </text>
          <text x='39' y='20' textAnchor='end' color='#FFFFFF' fill='#FFFFFF'>
            {' '}
            T
          </text>
        </g>
      </FobToteSwitchStyled>
    </div>
  )
}

// ================
// Local Components
// ================

const ToteOdds: React.FC<{
  acceptor: IAcceptorBase
  hidePlacePrice?: boolean
}> = ({ acceptor, hidePlacePrice }) => {
  return isToteSelectionEnabled(acceptor) && isRaceOpen(getRaceDetail()) ? (
    <OddsPriceButtonStyled
      onClick={() => SelectAcceptor({ acceptorNumber: acceptor.number, isFixed: false })}
    >
      <ToteOddDisplay acceptor={acceptor} hidePlacePrice={hidePlacePrice} />
    </OddsPriceButtonStyled>
  ) : (
    <OddspriceDisabledStyled className='oddspriceDisabled'>
      <ToteOddDisplay acceptor={acceptor} hidePlacePrice={hidePlacePrice} />
    </OddspriceDisabledStyled>
  )
}

const FixedOdds: React.FC<{
  acceptor: IAcceptorBase
  hidePlacePrice?: boolean
}> = ({ acceptor, hidePlacePrice }) => {
  return isFobSelectionEnabled(acceptor) && isRaceOpen(getRaceDetail()) ? (
    <OddsPriceButtonStyled
      onClick={() => SelectAcceptor({ acceptorNumber: acceptor.number, isFixed: true })}
    >
      <FixedOddsDisplay acceptor={acceptor} hidePlacePrice={hidePlacePrice} />
    </OddsPriceButtonStyled>
  ) : (
    <OddspriceDisabledStyled className='oddspriceDisabled'>
      <FixedOddsDisplay acceptor={acceptor} hidePlacePrice={hidePlacePrice} />
    </OddspriceDisabledStyled>
  )
}

const ToteOddDisplay: React.FC<{
  acceptor: IAcceptorBase
  hidePlacePrice?: boolean
}> = ({ acceptor: { isScratched, scratchType, toteOdds }, hidePlacePrice }) => {
  if (typeof isScratched === 'boolean' && isScratched) {
    return <span data-tid-tote-odds-win=''>{scratchStatus(scratchType)}</span>
  }
  if (hidePlacePrice) {
    return (
      <span>
        <Odds odds={toteOdds.win} isTote={true} />
      </span>
    )
  } else {
    return (
      <span>
        <Odds odds={toteOdds.win} isTote={true} />
        <br />
        <Odds odds={toteOdds.place} isTote={true} />
      </span>
    )
  }
}

const FixedOddsDisplay: React.FC<{ acceptor: IAcceptorBase; hidePlacePrice?: boolean }> = ({
  acceptor: { scratchType, fixedOdds },
  hidePlacePrice,
}) => {
  if (fixedOdds !== undefined && fixedOdds.isScratched) {
    return <span data-tid-fixed-odds-win=''>{scratchStatus(scratchType)}</span>
  }
  if (fixedOdds !== undefined && fixedOdds.isSuspended) {
    return <span data-tid-fixed-odds-win=''>-</span>
  }
  if (hidePlacePrice) {
    return (
      <span>
        <Odds odds={fixedOdds.win} isTote={false} />
      </span>
    )
  } else {
    return (
      <span>
        <Odds odds={fixedOdds.win} isTote={false} />
        <br />
        <Odds odds={fixedOdds.place} isTote={false} />
      </span>
    )
  }
}

const Odds: React.FC<{ odds: string; isTote: boolean }> = ({ odds, isTote }) => {
  const testIdProps = isTote ? { 'data-tid-tote-odds-win': '' } : { 'data-tid-fixed-odds-win': '' }

  if (!odds || odds === '' || odds === '-' || odds === null || odds === undefined) {
    return <span {...testIdProps}>-</span>
  }

  const value: number = Number.isNaN(Number(odds)) ? 0 : Number(odds)
  return <span {...testIdProps}>{value.toFixed(2)}</span>
}

function scratchStatus(scratchType: string): JSX.Element {
  if (scratchType === 'Standard') {
    return <div className='red prhalfem'>SCR</div>
  } else if (scratchType === 'Late') {
    return <div className='red prhalfem'>LATE SCR</div>
  } else {
    return <span>'-'</span>
  }
}

// =============
// Local Helpers
// =============

function isRaceOpen(raceDetail: IRaceDetail | null) {
  return raceDetail ? raceDetail.raceStatus === 'Open' : true
}

function isFobSelectionEnabled({
  fixedOdds,
  hasJumped,
}: {
  isScratched: boolean
  fixedOdds: IFixedOdds
  hasJumped: boolean
}): boolean {
  // explicitly testing for false..
  // - fixedOdds.isScratched to ensure good things (button) is not enabled, i.e. since good things fixedOdds.isScratched=undefined
  // - hasJumped to support backward compatibility against old mobi v94, i.e. old mobi hasJumped=undefined
  return (
    hasJumped === false &&
    fixedOdds !== undefined &&
    !fixedOdds.isSuspended &&
    fixedOdds.win !== undefined &&
    fixedOdds.isScratched === false
  )
}

function isToteSelectionEnabled({
  isScratched,
  hasJumped,
}: {
  isScratched: boolean
  hasJumped: boolean
}): boolean {
  // explicitly testing for false..
  // - isScratched to ensure good things (button) is not enabled, i.e. since good things isScratched=undfined
  // - hasJumped to support backward compatibility against old mobi v94, i.e. old mobi hasJumped=undefined
  return isScratched === false && hasJumped === false
}

// ======
// Styles
// ======

const OddsPriceButtonStyled = styled.button({
  WebkitTapHighlightColor: 'transparent',
  background: 'linear-gradient(to left bottom, #fff, #f4f4f4)',
  borderRadius: '.3em',
  border: '1px solid #ccc',
  color: '#000-light',
  display: 'inline-block',
  fontSize: 'inherit',
  fontWeight: 'bold',
  padding: '.8rem .6em .8rem 0',
  textAlign: 'right',
  lineHeight: '1.4em',
  verticalAlign: 'middle',
  width: '50%',
})

const OddspriceDisabledStyled = styled.div({
  verticalAlign: 'middle',
  textAlign: 'center',
  display: 'inline-block',
  width: '50%',
  minHeight: '4.2em',
})

const OddsContainerStyled = styled.div({
  maxWidth: '4em',
  width: '100%',
  margin: '0 auto',

  '.odds': {
    width: '200%',
    transitionProperty: 'transform',
    transitionDuration: '.25s',
    transitionTimingFunction: 'ease-in-out',
  },

  '.oddsToggle': {
    transitionProperty: 'cx',
    transitionDuration: '.2s',
    transitionTimingFunction: 'ease-in-out',
  },
})

const FobToteSwitchStyled = styled.svg({
  WebkitBackfaceVisibility: 'hidden',
  backfaceVisibility: 'hidden',
  display: 'inline-block',
  height: 30,
  padding: 2,
  verticalAlign: 'middle',
  width: 50,

  '@media only screen and (min-device-width: 768px)': {
    width: '65px',
    height: '40px',
  },
})
